import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, ChevronDown, ChevronRight, Copy, SplitSquareHorizontal, Settings2, Upload, Ruler, Package, GripVertical } from "lucide-react";
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function SortableModRow({ mod, allSizes, openEditMod, deleteMod }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: mod.modifier_id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1, zIndex: isDragging ? 10 : "auto" };
  return (
    <div ref={setNodeRef} style={style} {...attributes} className={`grid grid-cols-[minmax(0,1fr)_60px_60px_60px_60px_70px_70px_80px] gap-2 items-center px-4 py-2 border-b border-slate-50 hover:bg-slate-50 group ${isDragging ? "bg-blue-50 shadow-md ring-1 ring-blue-300" : ""}`} data-testid={`mod-row-${mod.modifier_id}`}>
      <div className="flex items-center gap-1.5 min-w-0">
        <button {...listeners} className="cursor-grab active:cursor-grabbing p-0.5 text-slate-300 hover:text-slate-500 touch-none flex-shrink-0" data-testid={`drag-mod-${mod.modifier_id}`}>
          <GripVertical className="h-3.5 w-3.5" />
        </button>
        {mod.image_url && <img src={mod.image_url} alt="" className="h-7 w-7 rounded object-cover border border-slate-200 flex-shrink-0" />}
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-[var(--brand-primary)]">{mod.name}</span>
            {mod.price > 0 && <span className="text-sm text-slate-500">${mod.price.toFixed(2)}</span>}
          </div>
          {mod.size_offsets?.length > 0 && (
            <div className="flex gap-1 mt-0.5 flex-wrap">
              {mod.size_offsets.map((so) => {
                const sz = allSizes.find((s) => s.size_id === so.size_id);
                return <span key={so.size_id} className="text-[10px] bg-slate-100 text-slate-500 px-1 rounded">{sz?.short_name || "?"}: ${so.price.toFixed(2)}</span>;
              })}
            </div>
          )}
          {mod.included_items?.length > 0 && (
            <div className="flex items-center gap-1 mt-0.5">
              <Package className="h-3 w-3 text-amber-500" />
              <span className="text-[10px] text-amber-600">{mod.included_items.length} included item{mod.included_items.length !== 1 ? "s" : ""}</span>
            </div>
          )}
        </div>
      </div>
      <div className="text-center"><Checkbox checked={mod.is_active} disabled className="border-slate-300" /></div>
      <div className="text-center text-sm text-slate-600">{mod.default_qty}</div>
      <div className="text-center text-sm text-slate-600">{mod.max_qty}</div>
      <div className="text-center text-sm text-slate-600">{mod.sort_order}</div>
      <div className="text-center">
        {mod.allow_split ? <Badge variant="outline" className="text-xs text-emerald-600 border-emerald-200"><SplitSquareHorizontal className="h-3 w-3 mr-1" />Yes</Badge> : <span className="text-xs text-slate-300">No</span>}
      </div>
      <div className="text-center">
        {mod.allow_split && <Badge variant={mod.split_price_type === "half" ? "default" : "secondary"} className="text-xs">{mod.split_price_type === "half" ? "Half $" : "Full $"}</Badge>}
      </div>
      <div className="text-right flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        <button onClick={() => openEditMod(mod)} className="p-1 rounded hover:bg-blue-100" data-testid={`edit-mod-${mod.modifier_id}`}><Pencil className="h-3.5 w-3.5 text-[var(--brand-primary)]" /></button>
        <button onClick={() => deleteMod(mod.modifier_id)} className="p-1 rounded hover:bg-red-100" data-testid={`del-mod-${mod.modifier_id}`}><Trash2 className="h-3.5 w-3.5 text-red-500" /></button>
      </div>
    </div>
  );
}

export default function ModifiersPage() {
  const [groups, setGroups] = useState([]);
  const [allSizes, setAllSizes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedGroups, setExpandedGroups] = useState({});

  // Group dialog
  const [showGroupDialog, setShowGroupDialog] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [groupForm, setGroupForm] = useState({
    name: "", min_select: 0, max_select: 0, free_amount: 0,
    free_type: "free", jump_to_next: false, sort_order: 0, is_active: true, image_url: "",
  });

  // Modifier dialog
  const [showModDialog, setShowModDialog] = useState(false);
  const [editingMod, setEditingMod] = useState(null);
  const [modGroupId, setModGroupId] = useState("");
  const [modForm, setModForm] = useState({
    name: "", price: "", default_qty: 0, max_qty: 0, sort_order: 0,
    is_active: true, allow_split: false, split_price_type: "full", image_url: "",
    size_offsets: [], included_items: [],
  });

  const [allProducts, setAllProducts] = useState([]);

  const [uploadingFor, setUploadingFor] = useState(null);

  // Copy dialog
  const [showCopyDialog, setShowCopyDialog] = useState(false);
  const [copyGroupId, setCopyGroupId] = useState("");
  const [productTree, setProductTree] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [treeExpanded, setTreeExpanded] = useState({});

  const fetchGroups = useCallback(async () => {
    try {
      const [gRes, sRes, pRes] = await Promise.all([api.get("/modifier-groups"), api.get("/sizes"), api.get("/products?active_only=true")]);
      setGroups(gRes.data);
      setAllSizes(sRes.data.filter((s) => s.is_active));
      setAllProducts(pRes.data);
    } catch { toast.error("Failed to load modifier groups"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchGroups(); }, [fetchGroups]);

  const toggleGroup = (id) => setExpandedGroups((p) => ({ ...p, [id]: !p[id] }));

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const handleModDragEnd = async (event, mods) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIdx = mods.findIndex((m) => m.modifier_id === active.id);
    const newIdx = mods.findIndex((m) => m.modifier_id === over.id);
    if (oldIdx === -1 || newIdx === -1) return;
    const reordered = arrayMove(mods, oldIdx, newIdx);
    const items = reordered.map((m, i) => ({ modifier_id: m.modifier_id, sort_order: i + 1 }));
    try {
      await api.put("/modifiers/batch-reorder", { items });
      fetchGroups();
    } catch { toast.error("Failed to reorder"); }
  };

  // === Group CRUD ===
  const openAddGroup = () => {
    setEditingGroup(null);
    setGroupForm({ name: "", min_select: 0, max_select: 0, free_amount: 0, free_type: "free", jump_to_next: false, sort_order: groups.length + 1, is_active: true, image_url: "" });
    setShowGroupDialog(true);
  };
  const openEditGroup = (g) => {
    setEditingGroup(g);
    setGroupForm({ name: g.name, min_select: g.min_select, max_select: g.max_select, free_amount: g.free_amount, free_type: g.free_type || "free", jump_to_next: g.jump_to_next || false, sort_order: g.sort_order || 0, is_active: g.is_active !== false, image_url: g.image_url || "" });
    setShowGroupDialog(true);
  };
  const saveGroup = async () => {
    try {
      const payload = { ...groupForm, min_select: parseInt(groupForm.min_select) || 0, max_select: parseInt(groupForm.max_select) || 0, free_amount: parseInt(groupForm.free_amount) || 0, sort_order: parseInt(groupForm.sort_order) || 0 };
      if (editingGroup) { await api.put(`/modifier-groups/${editingGroup.modifier_group_id}`, payload); toast.success("Updated"); }
      else { await api.post("/modifier-groups", payload); toast.success("Created"); }
      setShowGroupDialog(false); fetchGroups();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };
  const deleteGroup = async (id) => {
    if (!window.confirm("Delete this modifier group and all its modifiers?")) return;
    try { await api.delete(`/modifier-groups/${id}`); toast.success("Deleted"); fetchGroups(); }
    catch { toast.error("Failed"); }
  };

  // === Modifier CRUD ===
  const openAddMod = (groupId) => {
    setEditingMod(null); setModGroupId(groupId);
    const g = groups.find((x) => x.modifier_group_id === groupId);
    setModForm({ name: "", price: "", default_qty: 0, max_qty: 0, sort_order: (g?.modifiers?.length || 0) + 1, is_active: true, allow_split: false, split_price_type: "full", image_url: "", size_offsets: [], included_items: [] });
    setShowModDialog(true);
  };
  const openEditMod = (mod) => {
    setEditingMod(mod); setModGroupId(mod.modifier_group_id);
    setModForm({ name: mod.name, price: String(mod.price || ""), default_qty: mod.default_qty || 0, max_qty: mod.max_qty || 0, sort_order: mod.sort_order || 0, is_active: mod.is_active !== false, allow_split: mod.allow_split || false, split_price_type: mod.split_price_type || "full", image_url: mod.image_url || "", size_offsets: mod.size_offsets || [], included_items: mod.included_items || [] });
    setShowModDialog(true);
  };
  const saveMod = async () => {
    try {
      const payload = { ...modForm, modifier_group_id: modGroupId, price: parseFloat(modForm.price) || 0, default_qty: parseInt(modForm.default_qty) || 0, max_qty: parseInt(modForm.max_qty) || 0, sort_order: parseInt(modForm.sort_order) || 0 };
      if (editingMod) { await api.put(`/modifiers/${editingMod.modifier_id}`, payload); toast.success("Updated"); }
      else { await api.post("/modifiers", payload); toast.success("Created"); }
      setShowModDialog(false); fetchGroups();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };
  const deleteMod = async (id) => {
    if (!window.confirm("Delete this modifier?")) return;
    try { await api.delete(`/modifiers/${id}`); toast.success("Deleted"); fetchGroups(); }
    catch { toast.error("Failed"); }
  };

  // === Copy to Products ===
  const openCopyDialog = async (groupId) => {
    setCopyGroupId(groupId); setSelectedProducts([]);
    try {
      const res = await api.get("/products/tree");
      setProductTree(res.data);
    } catch { toast.error("Failed to load products"); }
    setShowCopyDialog(true);
  };
  const toggleProductSelect = (pid) => {
    setSelectedProducts((prev) => prev.includes(pid) ? prev.filter((x) => x !== pid) : [...prev, pid]);
  };
  const handleCopy = async () => {
    if (selectedProducts.length === 0) { toast.error("Select at least one product"); return; }
    try {
      await api.post(`/modifier-groups/${copyGroupId}/copy-to-products`, { product_ids: selectedProducts });
      toast.success(`Assigned to ${selectedProducts.length} products`);
      setShowCopyDialog(false);
    } catch { toast.error("Failed to copy"); }
  };

  // Image upload handler for both groups and modifiers
  const handleImageUpload = async (e, target) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please select an image"); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error("Max 5MB"); return; }
    setUploadingFor(target);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await api.post("/upload/image", formData, { headers: { "Content-Type": "multipart/form-data" } });
      const imageUrl = `${BACKEND_URL}/api/files/${res.data.storage_path}`;
      if (target === "group") setGroupForm((prev) => ({ ...prev, image_url: imageUrl }));
      else setModForm((prev) => ({ ...prev, image_url: imageUrl }));
      toast.success("Image uploaded!");
    } catch (err) { toast.error("Upload failed"); }
    finally { setUploadingFor(null); }
  };

  if (loading) return <div className="p-8 flex justify-center"><div className="animate-spin rounded-full h-8 w-8 border-2 border-[var(--brand-primary)] border-t-transparent" /></div>;

  return (
    <div className="p-6" data-testid="modifiers-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900 tracking-tight" data-testid="modifiers-title">Modifiers</h1>
          <p className="text-sm text-slate-500 mt-1">{groups.length} modifier groups</p>
        </div>
        <Button onClick={openAddGroup} className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-hover)] text-white" data-testid="add-mod-group-btn">
          <Plus className="h-4 w-4 mr-1" /> Add Modifier Group
        </Button>
      </div>

      {/* Groups List */}
      <div className="space-y-4" data-testid="modifier-groups-list">
        {groups.map((group) => {
          const isExpanded = expandedGroups[group.modifier_group_id];
          return (
            <div key={group.modifier_group_id} className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid={`mod-group-${group.modifier_group_id}`}>
              {/* Group Header */}
              <div className="flex items-center gap-3 px-4 py-3 bg-slate-50 cursor-pointer hover:bg-slate-100 transition-colors" onClick={() => toggleGroup(group.modifier_group_id)}>
                {isExpanded ? <ChevronDown className="h-4 w-4 text-slate-500" /> : <ChevronRight className="h-4 w-4 text-slate-500" />}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    {group.image_url ? <img src={group.image_url} alt="" className="h-6 w-6 rounded object-cover border border-slate-200" /> : <Settings2 className="h-4 w-4 text-[var(--brand-primary)]" />}
                    <span className="font-semibold text-sm text-slate-800">{group.name}</span>
                    {!group.is_active && <Badge variant="secondary" className="text-xs">Inactive</Badge>}
                  </div>
                  <div className="flex gap-4 mt-1 text-xs text-slate-400">
                    <span>Min: {group.min_select}</span>
                    <span>Max: {group.max_select}</span>
                    <span>Free: {group.free_amount}</span>
                    <span>{group.modifiers?.length || 0} modifiers</span>
                  </div>
                </div>
                <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openCopyDialog(group.modifier_group_id)} data-testid={`copy-mod-group-${group.modifier_group_id}`} title="Copy to products">
                    <Copy className="h-3.5 w-3.5 text-slate-500" />
                  </Button>
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEditGroup(group)} data-testid={`edit-mod-group-${group.modifier_group_id}`}>
                    <Pencil className="h-3.5 w-3.5 text-[var(--brand-primary)]" />
                  </Button>
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => deleteGroup(group.modifier_group_id)} data-testid={`del-mod-group-${group.modifier_group_id}`}>
                    <Trash2 className="h-3.5 w-3.5 text-red-500" />
                  </Button>
                </div>
              </div>

              {/* Modifiers Table */}
              {isExpanded && (
                <div className="border-t border-slate-100">
                  {/* Column Headers */}
                  <div className="grid grid-cols-[minmax(0,1fr)_60px_60px_60px_60px_70px_70px_80px] gap-2 px-4 py-2 text-xs font-semibold text-slate-400 uppercase tracking-wider bg-white border-b border-slate-100">
                    <div>Available Modifiers</div>
                    <div className="text-center">Active</div>
                    <div className="text-center">DFLT QTY</div>
                    <div className="text-center">MAX QTY</div>
                    <div className="text-center">Sort</div>
                    <div className="text-center">Split</div>
                    <div className="text-center">Split $</div>
                    <div className="text-right">Actions</div>
                  </div>

                  <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={(e) => handleModDragEnd(e, group.modifiers || [])}>
                    <SortableContext items={(group.modifiers || []).map((m) => m.modifier_id)} strategy={verticalListSortingStrategy}>
                      {group.modifiers?.map((mod) => (
                        <SortableModRow key={mod.modifier_id} mod={mod} allSizes={allSizes} openEditMod={openEditMod} deleteMod={deleteMod} />
                      ))}
                    </SortableContext>
                  </DndContext>

                  <div className="px-4 py-2">
                    <button onClick={() => openAddMod(group.modifier_group_id)} className="text-xs text-[var(--brand-primary)] hover:underline font-medium" data-testid={`add-mod-in-${group.modifier_group_id}`}>+ Add Modifier</button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* === GROUP DIALOG === */}
      <Dialog open={showGroupDialog} onOpenChange={setShowGroupDialog}>
        <DialogContent className="sm:max-w-lg" data-testid="mod-group-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{editingGroup ? "Edit Modifier Group" : "New Modifier Group"}</DialogTitle>
            <DialogDescription className="text-sm text-slate-500">Modifier groups define sets of options customers can choose from</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-2">
            <div>
              <Label className="text-xs text-slate-500">Group Name</Label>
              <Input value={groupForm.name} onChange={(e) => setGroupForm({ ...groupForm, name: e.target.value })} placeholder="e.g., Pizza Size, Toppings" data-testid="mod-group-name-input" />
            </div>
            <div>
              <Label className="text-xs text-slate-500">Image</Label>
              <div className="flex items-center gap-3 mt-1">
                <label className="cursor-pointer">
                  <input type="file" accept="image/*" onChange={(e) => handleImageUpload(e, "group")} className="hidden" data-testid="mod-group-image-upload" />
                  <div className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded border border-[var(--brand-primary)] text-xs font-medium text-[var(--brand-primary)] hover:bg-blue-50 ${uploadingFor === "group" ? "opacity-50" : ""}`}>
                    <Upload className="h-3.5 w-3.5" />{uploadingFor === "group" ? "Uploading..." : "Upload"}
                  </div>
                </label>
                <Input value={groupForm.image_url} onChange={(e) => setGroupForm({ ...groupForm, image_url: e.target.value })} placeholder="or paste URL" className="flex-1 h-8 text-xs" />
                {groupForm.image_url && <img src={groupForm.image_url} alt="" className="h-8 w-8 rounded object-cover border border-slate-200" />}
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label className="text-xs text-slate-500">Min Select</Label>
                <Input type="number" value={groupForm.min_select} onChange={(e) => setGroupForm({ ...groupForm, min_select: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs text-slate-500">Max Select</Label>
                <Input type="number" value={groupForm.max_select} onChange={(e) => setGroupForm({ ...groupForm, max_select: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs text-slate-500">Free Amount</Label>
                <Input type="number" value={groupForm.free_amount} onChange={(e) => setGroupForm({ ...groupForm, free_amount: e.target.value })} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs text-slate-500">Free Type</Label>
                <Select value={groupForm.free_type} onValueChange={(v) => setGroupForm({ ...groupForm, free_type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="free">Free</SelectItem>
                    <SelectItem value="included">Included</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-slate-500">Sort Order</Label>
                <Input type="number" value={groupForm.sort_order} onChange={(e) => setGroupForm({ ...groupForm, sort_order: e.target.value })} />
              </div>
            </div>
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <Switch checked={groupForm.jump_to_next} onCheckedChange={(v) => setGroupForm({ ...groupForm, jump_to_next: v })} />
                <Label className="text-sm">Jump to next group when max selected</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={groupForm.is_active} onCheckedChange={(v) => setGroupForm({ ...groupForm, is_active: v })} />
                <Label className="text-sm">Active</Label>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowGroupDialog(false)}>Cancel</Button>
            <Button onClick={saveGroup} className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-hover)] text-white" data-testid="mod-group-save-btn">{editingGroup ? "Update" : "Create"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* === MODIFIER DIALOG === */}
      <Dialog open={showModDialog} onOpenChange={setShowModDialog}>
        <DialogContent className="sm:max-w-lg" data-testid="modifier-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{editingMod ? "Edit Modifier" : "New Modifier"}</DialogTitle>
            <DialogDescription className="text-sm text-slate-500">Individual modifier option within a group</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-2">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs text-slate-500">Name</Label>
                <Input value={modForm.name} onChange={(e) => setModForm({ ...modForm, name: e.target.value })} placeholder="e.g., Large 18&quot;" data-testid="mod-name-input" />
              </div>
              <div>
                <Label className="text-xs text-slate-500">Price ($)</Label>
                <Input type="number" step="0.01" value={modForm.price} onChange={(e) => setModForm({ ...modForm, price: e.target.value })} data-testid="mod-price-input" />
              </div>
            </div>
            <div>
              <Label className="text-xs text-slate-500">Image</Label>
              <div className="flex items-center gap-3 mt-1">
                <label className="cursor-pointer">
                  <input type="file" accept="image/*" onChange={(e) => handleImageUpload(e, "modifier")} className="hidden" data-testid="mod-image-upload" />
                  <div className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded border border-[var(--brand-primary)] text-xs font-medium text-[var(--brand-primary)] hover:bg-blue-50 ${uploadingFor === "modifier" ? "opacity-50" : ""}`}>
                    <Upload className="h-3.5 w-3.5" />{uploadingFor === "modifier" ? "Uploading..." : "Upload"}
                  </div>
                </label>
                <Input value={modForm.image_url} onChange={(e) => setModForm({ ...modForm, image_url: e.target.value })} placeholder="or paste URL" className="flex-1 h-8 text-xs" />
                {modForm.image_url && <img src={modForm.image_url} alt="" className="h-8 w-8 rounded object-cover border border-slate-200" />}
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label className="text-xs text-slate-500">Default Qty</Label>
                <Input type="number" value={modForm.default_qty} onChange={(e) => setModForm({ ...modForm, default_qty: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs text-slate-500">Max Qty</Label>
                <Input type="number" value={modForm.max_qty} onChange={(e) => setModForm({ ...modForm, max_qty: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs text-slate-500">Sort Order</Label>
                <Input type="number" value={modForm.sort_order} onChange={(e) => setModForm({ ...modForm, sort_order: e.target.value })} />
              </div>
            </div>

            {/* Split Configuration */}
            <div className="border border-slate-200 rounded-lg p-4 bg-slate-50">
              <div className="flex items-center gap-2 mb-3">
                <SplitSquareHorizontal className="h-4 w-4 text-[var(--brand-primary)]" />
                <span className="text-sm font-medium text-slate-700">Split Configuration</span>
              </div>
              <div className="flex items-center gap-6">
                <div className="flex items-center gap-2">
                  <Switch checked={modForm.allow_split} onCheckedChange={(v) => setModForm({ ...modForm, allow_split: v })} data-testid="mod-allow-split" />
                  <Label className="text-sm">Allow Half/Full Split</Label>
                </div>
                {modForm.allow_split && (
                  <div>
                    <Label className="text-xs text-slate-500 mb-1 block">When split, charge:</Label>
                    <Select value={modForm.split_price_type} onValueChange={(v) => setModForm({ ...modForm, split_price_type: v })}>
                      <SelectTrigger className="w-36" data-testid="mod-split-price-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="half">Half Price</SelectItem>
                        <SelectItem value="full">Full Price</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
              {modForm.allow_split && (
                <p className="text-xs text-slate-400 mt-2">
                  {modForm.split_price_type === "half"
                    ? "When placed on half the item, customer pays 50% of the modifier price"
                    : "When placed on half the item, customer still pays the full modifier price"}
                </p>
              )}
            </div>

            <div className="flex items-center gap-2">
              <Switch checked={modForm.is_active} onCheckedChange={(v) => setModForm({ ...modForm, is_active: v })} />
              <Label className="text-sm">Active</Label>
            </div>

            {/* Size Price Offsets */}
            {allSizes.length > 0 && (
              <div className="border border-slate-200 rounded-lg bg-slate-50" data-testid="mod-size-offsets">
                <button type="button" onClick={() => document.getElementById("size-offsets-body")?.classList.toggle("hidden")} className="flex items-center justify-between w-full px-4 py-2.5 text-left hover:bg-slate-100 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Ruler className="h-4 w-4 text-[var(--brand-primary)]" />
                    <span className="text-sm font-medium text-slate-700">Price per Size</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] text-slate-400">{modForm.size_offsets.length} set</span>
                    <ChevronDown className="h-4 w-4 text-slate-400" />
                  </div>
                </button>
                <div id="size-offsets-body" className="px-4 pb-3 hidden">
                  <p className="text-[10px] text-slate-500 mb-2">Base price used when no size is selected.</p>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-1.5">
                    {allSizes.map((size) => {
                      const existing = modForm.size_offsets.find((so) => so.size_id === size.size_id);
                      return (
                        <div key={size.size_id} className="flex items-center gap-1.5">
                          <span className="text-xs text-slate-600 truncate flex-1">{size.name}</span>
                          <Badge variant="outline" className="text-[9px] font-mono px-1 h-5">{size.short_name}</Badge>
                          <span className="text-[10px] text-slate-400">$</span>
                          <Input
                            type="number" step="0.01" placeholder="—"
                            className="h-6 text-xs w-16"
                            value={existing?.price ?? ""}
                            onChange={(e) => {
                              const val = e.target.value;
                              setModForm((prev) => {
                                const newOffsets = prev.size_offsets.filter((so) => so.size_id !== size.size_id);
                                if (val && parseFloat(val) >= 0) newOffsets.push({ size_id: size.size_id, price: parseFloat(val) });
                                return { ...prev, size_offsets: newOffsets };
                              });
                            }}
                            data-testid={`mod-size-offset-${size.size_id}`}
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

            {/* Included Items ("Comes With") */}
            <div className="border border-slate-200 rounded-lg bg-slate-50" data-testid="mod-included-items">
              <button type="button" onClick={() => document.getElementById("included-items-body")?.classList.toggle("hidden")} className="flex items-center justify-between w-full px-4 py-2.5 text-left hover:bg-slate-100 rounded-lg">
                <div className="flex items-center gap-2">
                  <Package className="h-4 w-4 text-[var(--brand-primary)]" />
                  <span className="text-sm font-medium text-slate-700">Included Items (Comes With)</span>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="text-xs">{modForm.included_items.length} item{modForm.included_items.length !== 1 ? "s" : ""}</Badge>
                  <ChevronDown className="h-4 w-4 text-slate-400" />
                </div>
              </button>
              <div id="included-items-body" className="px-4 pb-3 hidden">
                <p className="text-[10px] text-slate-500 mb-2">When selected, these items come with it. Customer can add extras at listed price.</p>

              {modForm.included_items.map((item, idx) => {
                const prod = allProducts.find((p) => p.product_id === item.product_id);
                return (
                  <div key={idx} className="border border-slate-200 rounded-lg p-2.5 mb-2 bg-white" data-testid={`included-item-${idx}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-sm font-medium text-slate-800 flex-1 truncate">{item.name || prod?.name || "Unknown"}</span>
                      <button onClick={() => setModForm((prev) => ({ ...prev, included_items: prev.included_items.filter((_, i) => i !== idx) }))} className="h-5 w-5 rounded bg-red-50 text-red-500 flex items-center justify-center hover:bg-red-100" data-testid={`remove-included-${idx}`}>
                        <Trash2 className="h-3 w-3" />
                      </button>
                    </div>
                    <div className="grid grid-cols-3 gap-2 mb-2">
                      <div>
                        <Label className="text-[10px] text-slate-400">Price per Extra</Label>
                        <Input
                          type="number" step="0.01" placeholder={prod?.price ? `$${prod.price.toFixed(2)}` : "0"}
                          className="h-6 text-xs"
                          value={item.price_each ?? ""}
                          onChange={(e) => {
                            const val = e.target.value;
                            setModForm((prev) => {
                              const items = [...prev.included_items];
                              items[idx] = { ...items[idx], price_each: val === "" ? null : parseFloat(val) };
                              return { ...prev, included_items: items };
                            });
                          }}
                          data-testid={`included-price-${idx}`}
                        />
                      </div>
                      <div>
                        <Label className="text-[10px] text-slate-400">Max Extra</Label>
                        <Input
                          type="number" min="0" max="20"
                          className="h-6 text-xs"
                          value={item.max_extra ?? 5}
                          onChange={(e) => setModForm((prev) => {
                            const items = [...prev.included_items];
                            items[idx] = { ...items[idx], max_extra: parseInt(e.target.value) || 0 };
                            return { ...prev, included_items: items };
                          })}
                          data-testid={`included-max-extra-${idx}`}
                        />
                      </div>
                      <div>
                        <Label className="text-[10px] text-slate-400">Default Qty</Label>
                        <Input
                          type="number" min="0" max="10"
                          className="h-6 text-xs"
                          value={item.default_qty ?? 1}
                          onChange={(e) => setModForm((prev) => {
                            const items = [...prev.included_items];
                            items[idx] = { ...items[idx], default_qty: parseInt(e.target.value) || 0 };
                            return { ...prev, included_items: items };
                          })}
                          data-testid={`included-default-qty-${idx}`}
                        />
                      </div>
                    </div>
                    {allSizes.length > 0 && (
                      <div>
                        <Label className="text-[10px] text-slate-400 mb-1 block">Qty per Size</Label>
                        <div className="grid grid-cols-2 gap-x-3 gap-y-1">
                          {allSizes.map((sz) => (
                            <div key={sz.size_id} className="flex items-center gap-1">
                              <span className="text-[10px] text-slate-500 truncate flex-1">{sz.name}</span>
                              <Badge variant="outline" className="text-[8px] font-mono px-0.5 h-4">{sz.short_name}</Badge>
                              <Input
                                type="number" min="0" max="20" placeholder="0"
                                className="h-5 text-[10px] w-10"
                                value={item.size_quantities?.[sz.size_id] ?? ""}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  setModForm((prev) => {
                                    const items = [...prev.included_items];
                                    const sq = { ...(items[idx].size_quantities || {}) };
                                    if (val && parseInt(val) > 0) sq[sz.size_id] = parseInt(val);
                                    else delete sq[sz.size_id];
                                    items[idx] = { ...items[idx], size_quantities: sq };
                                    return { ...prev, included_items: items };
                                  });
                                }}
                                data-testid={`included-sz-qty-${idx}-${sz.size_id}`}
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}

              {/* Add included item picker */}
              <Select
                value=""
                onValueChange={(pid) => {
                  const prod = allProducts.find((p) => p.product_id === pid);
                  if (!prod) return;
                  setModForm((prev) => ({
                    ...prev,
                    included_items: [...prev.included_items, {
                      product_id: pid,
                      name: prod.name,
                      price_each: prod.price || 0,
                      size_quantities: {},
                      default_qty: 1,
                      max_extra: 5,
                    }],
                  }));
                }}
              >
                <SelectTrigger className="h-8 text-xs border-dashed" data-testid="add-included-item-select">
                  <SelectValue placeholder="+ Add included item (pick product)..." />
                </SelectTrigger>
                <SelectContent>
                  {allProducts.filter((p) => !modForm.included_items.some((i) => i.product_id === p.product_id)).map((p) => (
                    <SelectItem key={p.product_id} value={p.product_id}>{p.name} — ${(p.price || 0).toFixed(2)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModDialog(false)}>Cancel</Button>
            <Button onClick={saveMod} className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-hover)] text-white" data-testid="mod-save-btn">{editingMod ? "Update" : "Create"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* === COPY DIALOG === */}
      <Dialog open={showCopyDialog} onOpenChange={setShowCopyDialog}>
        <DialogContent className="sm:max-w-2xl" data-testid="copy-modifiers-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Copy Modifiers to Products</DialogTitle>
            <DialogDescription className="text-sm text-slate-500">Select products to assign this modifier group to</DialogDescription>
          </DialogHeader>
          <ScrollArea className="h-[400px] border border-slate-200 rounded-lg p-3">
            {productTree.map((cat) => (
              <div key={cat.category_id} className="mb-2">
                <button onClick={() => setTreeExpanded((p) => ({ ...p, [cat.category_id]: !p[cat.category_id] }))} className="flex items-center gap-2 py-1 w-full text-left">
                  {treeExpanded[cat.category_id] ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                  <span className="text-sm font-semibold text-slate-700">{cat.name}</span>
                </button>
                {treeExpanded[cat.category_id] && (
                  <div className="pl-6 space-y-0.5">
                    {cat.subcategories?.map((sc) => (
                      <div key={sc.subcategory_id}>
                        <p className="text-xs font-medium text-slate-500 py-1">{sc.name}</p>
                        <div className="pl-4 space-y-0.5">
                          {sc.products?.map((p) => (
                            <label key={p.product_id} className="flex items-center gap-2 py-0.5 cursor-pointer hover:bg-slate-50 rounded px-1">
                              <Checkbox checked={selectedProducts.includes(p.product_id)} onCheckedChange={() => toggleProductSelect(p.product_id)} />
                              <span className="text-sm text-slate-700">{p.name}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    ))}
                    {cat.direct_products?.map((p) => (
                      <label key={p.product_id} className="flex items-center gap-2 py-0.5 cursor-pointer hover:bg-slate-50 rounded px-1">
                        <Checkbox checked={selectedProducts.includes(p.product_id)} onCheckedChange={() => toggleProductSelect(p.product_id)} />
                        <span className="text-sm text-slate-700">{p.name}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </ScrollArea>
          <DialogFooter>
            <p className="text-sm text-slate-500 mr-auto">{selectedProducts.length} products selected</p>
            <Button variant="outline" onClick={() => setShowCopyDialog(false)}>Cancel</Button>
            <Button onClick={handleCopy} className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-hover)] text-white" data-testid="copy-confirm-btn">
              <Copy className="h-4 w-4 mr-1" /> Copy
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
